import { useState, useEffect } from 'react';
import { getClient } from '@/lib/graphql/client';
import { GET_CHANNEL_OPS_MODE } from '@/lib/graphql/operations/qcommerce';
import { useAuth } from '@/lib/auth/auth-context';

const operationsModeCache = new Map<string, string>();

interface OpsModeState {
  channelId?: string;
  mode: string;
  loading: boolean;
}

export function useOpsMode() {
  const { activeChannel } = useAuth();
  const channelId = activeChannel?.id;
  const [state, setState] = useState<OpsModeState>(() => {
    const cached = channelId ? operationsModeCache.get(channelId) : undefined;
    return {
      channelId,
      mode: cached ?? 'standard',
      loading: Boolean(channelId && !cached),
    };
  });

  useEffect(() => {
    if (!channelId) {
      setState({ channelId: undefined, mode: 'standard', loading: false });
      return;
    }

    const cached = operationsModeCache.get(channelId);
    if (cached) {
      setState({ channelId, mode: cached, loading: false });
      return;
    }

    setState({ channelId, mode: 'standard', loading: true });
    let cancelled = false;

    const fetchMode = async () => {
      try {
        const client = getClient();
        const result = await client.query(GET_CHANNEL_OPS_MODE, {}).toPromise();
        const mode = result.data?.activeChannel?.customFields?.operationsMode ?? 'standard';
        operationsModeCache.set(channelId, mode);
        if (!cancelled) setState({ channelId, mode, loading: false });
      } catch {
        if (!cancelled) setState({ channelId, mode: 'standard', loading: false });
      }
    };
    void fetchMode();

    return () => {
      cancelled = true;
    };
  }, [channelId]);

  const isCurrentChannel = state.channelId === channelId;
  const operationsMode = isCurrentChannel ? state.mode : 'standard';
  const loading = Boolean(channelId && (!isCurrentChannel || state.loading));

  return { operationsMode, isOpsEnabled: operationsMode === 'advanced', loading };
}

/** Invalidate after a channel switch or platform-assisted mode change. */
export function invalidateOpsModeCache() {
  operationsModeCache.clear();
}
